import React, { useState } from 'react';
import {
  FaTimes,
  FaExclamationTriangle,
  FaInfoCircle,
  FaCheck,
  FaTextHeight,
  FaList,
  FaUserCheck,
  FaStickyNote
} from 'react-icons/fa';
import styles from './RejectionModal.module.css';

const REJECTION_CATEGORIES = [
  { value: 'incomplete_application', label: 'Incomplete Application' },
  { value: 'does_not_meet_requirements', label: 'Does Not Meet Requirements' },
  { value: 'club_full', label: 'Club Full' },
  { value: 'skill_level_mismatch', label: 'Skill Level Mismatch' },
  { value: 'behavioral_concerns', label: 'Behavioral Concerns' },
  { value: 'other', label: 'Other' }
];

const RejectionModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  memberName, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    reason: '',
    category: '',
    allowReapplication: true,
    additionalNotes: ''
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason is required';
    } else if (formData.reason.trim().length < 10) {
      newErrors.reason = 'Reason must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const rejectionData = {
      reason: formData.reason.trim(),
      ...(formData.category && { category: formData.category }),
      allowReapplication: formData.allowReapplication,
      ...(formData.additionalNotes.trim() && { additionalNotes: formData.additionalNotes.trim() })
    };

    onConfirm(rejectionData);
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        reason: '',
        category: '',
        allowReapplication: true,
        additionalNotes: ''
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerIcon}>
            <FaExclamationTriangle className={styles.warningIcon} />
          </div>
          <div className={styles.headerText}>
            <h2>Reject Membership Application</h2>
            <p>Provide details for rejecting {memberName}'s application</p>
          </div>
          <button 
            className={styles.closeButton} 
            onClick={handleClose}
            disabled={isLoading}
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalBody}>
          {/* Reason Field - Required */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <FaTextHeight className={styles.labelIcon} />
              Reason for Rejection *
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              placeholder="Please provide a clear reason for rejecting this application..."
              className={`${styles.textarea} ${errors.reason ? styles.error : ''}`}
              rows={4}
              disabled={isLoading}
            />
            {errors.reason && (
              <span className={styles.errorMessage}>{errors.reason}</span>
            )}
            <div className={styles.fieldHint}>
              <FaInfoCircle className={styles.hintIcon} />
              This will be included in the notification sent to the applicant
            </div>
          </div>

          {/* Category Field - Optional */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <FaList className={styles.labelIcon} />
              Rejection Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className={styles.select}
              disabled={isLoading}
            >
              <option value="">Select a category (optional)</option>
              {REJECTION_CATEGORIES.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
            <div className={styles.fieldHint}>
              <FaInfoCircle className={styles.hintIcon} />
              Helps categorize rejections for analytics and reporting
            </div>
          </div>

          {/* Allow Reapplication Checkbox */}
          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.allowReapplication}
                onChange={(e) => handleInputChange('allowReapplication', e.target.checked)}
                className={styles.checkbox}
                disabled={isLoading}
              />
              <span className={styles.checkboxCustom}>
                {formData.allowReapplication && <FaCheck className={styles.checkIcon} />}
              </span>
              <div className={styles.checkboxText}>
                <FaUserCheck className={styles.labelIcon} />
                Allow future reapplication
              </div>
            </label>
            <div className={styles.fieldHint}>
              <FaInfoCircle className={styles.hintIcon} />
              {formData.allowReapplication 
                ? "The applicant will be able to apply again in the future"
                : "The applicant will be permanently blocked from reapplying"
              }
            </div>
          </div>

          {/* Additional Notes Field - Optional */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <FaStickyNote className={styles.labelIcon} />
              Additional Notes
            </label>
            <textarea
              value={formData.additionalNotes}
              onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
              placeholder="Any additional feedback or suggestions for the applicant..."
              className={styles.textarea}
              rows={3}
              disabled={isLoading}
            />
            <div className={styles.fieldHint}>
              <FaInfoCircle className={styles.hintIcon} />
              Optional feedback that will be included in the notification
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.rejectButton}
              disabled={isLoading || !formData.reason.trim()}
            >
              {isLoading ? (
                <>
                  <div className={styles.spinner}></div>
                  Rejecting...
                </>
              ) : (
                <>
                  <FaExclamationTriangle />
                  Reject Application
                </>
              )}
            </button>
          </div>
        </form>

        {/* Warning Notice */}
        <div className={styles.warningNotice}>
          <FaInfoCircle className={styles.noticeIcon} />
          <div className={styles.noticeText}>
            <strong>Important:</strong> The applicant will receive an email notification with the rejection details. 
            This action cannot be undone, but they may reapply if permitted.
          </div>
        </div>
      </div>
    </div>
  );
};

export default RejectionModal;


